import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { omitBy } from 'lodash';
import { Text } from '@chakra-ui/react';
import {
  deleteDatas,
  deleteInputs,
  deleteRoles,
  deleteStats,
  deleteUsers,
  patchData,
  patchRoles,
  updateDocumentsList,
  updateInputs,
  updateUsers,
} from '../../../api/tabsApi';
import useForm from '../../form/Form';
import { TColumnData } from '../../tabs/type';
import { TState, useActions } from '../../../redux/store';
import { useCanI, useSubTabs } from '../../../redux/actions';
import { RoleTree } from './ManageRoles';
import Modal from '../../modal/Modal';
import { usePageTree } from './ManageDocuments';

const ManageActions = () => {
  const tabToUpdate = useSelector((state: TState) => state.tabToUpdate);
  const currentTab = useSelector((state: TState) => state.currentTab);
  const rowSelected = useSelector((state: TState) => state.rowSelected);
  const roleList = useSelector((state: TState) => state.roleList);
  const rowData = useSelector((state: TState) => state.rowData);
  const isSuper = useSelector((state: TState) => state.isSuper);
  const isAdmin = useSelector((state: TState) => state.isAdmin);
  const modalData = useSelector((state: TState) => state.modalData);
  const {
    setRowSelected,
    onClose,
    getInputs,
    getRolesList,
    getUsersList,
    getData,
    getDocumentList,
  } = useActions();
  const { PageTree, selected } = usePageTree(true);

  const canI = useCanI();
  const subTabs = useSubTabs();

  let inputs = subTabs.reduce(
    (prev, tab) => [
      ...prev,
      ...tab.tableColumns
        .flatMap((tb) => tb.tableColumns)
        .filter(
          (col) =>
            (/select|users/.test(col.type) ||
              (/switch/.test(col.type) && col.label)) &&
            !prev.find((p) => p._id === col._id),
        )
        .map((col) => ({
          ...col,
          parent_id: tab._id,
          type: col.type === 'switch' ? 'tripleChoice' : col.type,
          required: false,
          advised: false,
          withEmpty: true,
        })),
    ],
    [] as TColumnData[],
  );

  const options = roleList?.map((role) => ({
    label: role.name,
    value: role._id,
  }));

  const userInputs = [
    {
      label: 'Forfait',
      name: 'plan',
      type: 'select',
      options: [
        { label: 'basic', value: 'basic' },
        { label: 'pro light', value: 'proLight' },
        { label: 'pro', value: 'pro' },
        { label: 'entreprise', value: 'entreprise' },
      ],
      condition: () => isSuper,
    },
    {
      label: 'Role',
      name: 'role_id',
      type: 'multiselect',
      options,
      selected: rowData.role_id || [],
      condition: () => !isSuper,
    },
    {
      label: 'Utilisateurs supervisés',
      name: 'assignedUsers',
      type: 'users',
      isMulti: true,
      selected: rowData.assignedUsers,
      condition: () => !isSuper,
    },
    {
      label: 'Admin',
      name: 'isAdmin',
      type: 'tripleChoice',
      condition: () => isSuper || isAdmin,
    },
    {
      label: 'Planifiable',
      name: 'isPlanable',
      type: 'tripleChoice',
    },
    {
      label: 'Voir les RDV non confirmé',
      name: 'seeNoConfirm',
      type: 'tripleChoice',
    },
    { label: 'Désactivé', name: 'blocked', type: 'tripleChoice' },
    {
      label: 'Sans restriction',
      name: 'noRestrict',
      type: 'tripleChoice',
    },
    {
      label: 'Sans notifications',
      name: 'noNotif',
      type: 'tripleChoice',
    },
  ];

  const inputsInputs = [
    {
      type: 'tripleChoice',
      label: 'Champ obligatoire',
      name: 'required',
    },
    {
      type: 'tripleChoice',
      label: 'Champ conseilé',
      name: 'advised',
    },
    {
      type: 'tripleChoice',
      label: 'Champ avec colonne daté',
      name: 'dated',
    },
    {
      type: 'createSelect',
      label: 'Groupe',
      name: 'group',
    },
  ];

  if (tabToUpdate?.isDuplicate) {
    inputs = [
      {
        label: 'Retirer doublon',
        name: 'isDuplicate',
        type: 'checkbox',
      } as any,
    ];
  }

  const { Form: FormInputs, formData: formDataInputs } = useForm(inputsInputs);
  const { Form: FormUsers, formData: formDataUsers } = useForm(userInputs);
  const { Form, formData } = useForm(inputs);

  const [roles, setRoles] = useState({});

  const handleTreeChange = useCallback(
    (v: Record<string, boolean>) => {
      setRoles((allValues) => ({ ...allValues, ...v }));
    },
    [setRoles],
  );

  const isInputs = /inputs/.test(tabToUpdate?._id || '');
  const isRoles = /roles/.test(tabToUpdate?._id || '');
  const isUsers = /users/.test(tabToUpdate?._id || '');
  const isDocuments = /documents/.test(tabToUpdate?._id || '');
  const isStats = /stats/.test(tabToUpdate?._id || '');

  const handleAction = async () => {
    onClose();

    if (tabToUpdate?.isDuplicate && formData.isDuplicate === true) {
      formData.isDuplicate = false;
    }

    const rows = [...rowSelected];
    const promises: any[] = [];

    while (rows.length) {
      const batchRows = rows.splice(0, 2000);

      switch (true) {
        case isInputs:
          promises.push(updateInputs({ ids: batchRows, ...formDataInputs }));
          break;
        case isRoles:
          const filteredRoles = omitBy(roles, (r) => r === undefined);
          promises.push(patchRoles({ ids: batchRows, roles: filteredRoles }));
          break;
        case isUsers:
          promises.push(updateUsers({ ids: batchRows, ...formDataUsers }));
          break;
        case isDocuments:
          promises.push(updateDocumentsList(batchRows, selected));
          break;
        default:
          promises.push(patchData({ ids: batchRows, ...formData }));
      }
    }

    await Promise.all(promises);

    switch (true) {
      case isInputs:
        getInputs({ forced: true });
        break;
      case isRoles:
        getRolesList();
        break;
      case isUsers:
        getUsersList({ forced: true });
        break;
      case isDocuments:
        getDocumentList({ forced: true });
        break;
      default:
        getData();
    }

    setRowSelected([]);
  };

  const handleDeleteAction = async () => {
    onClose();

    const rows = [...rowSelected];
    const promises: any[] = [];

    while (rows.length) {
      const batchRows = rows.splice(0, 2000);

      switch (true) {
        case isInputs:
          promises.push(deleteInputs(batchRows));
          break;
        case isRoles:
          promises.push(deleteRoles(batchRows));
          break;
        case isUsers:
          promises.push(deleteUsers(batchRows));
          break;
        case isStats:
          promises.push(deleteStats(batchRows));
          break;
        default:
          promises.push(deleteDatas(batchRows));
      }
    }

    await Promise.all(promises);

    switch (true) {
      case isInputs:
        getInputs({ forced: true });
        break;
      case isRoles:
        getRolesList({ forced: true });
        break;
      case isUsers:
        getUsersList({ forced: true });
        break;
      case isStats:
        modalData.refreshData?.();
        break;
      default:
        getData();
    }

    setRowSelected([]);
  };

  const deletePermission =
    isAdmin || (currentTab?._id && canI(currentTab?._id, 'delete'));

  const renderDeleteInput = (
    <Text>
      Vous confirmez vouloir supprimer toutes les lignes sélectionnés ?
    </Text>
  );

  let renderManageActions: any = Form;

  switch (true) {
    case isInputs:
      renderManageActions = FormInputs;
      break;
    case isRoles:
      renderManageActions = (
        <RoleTree handleTreeChange={handleTreeChange} forcedAction />
      );
      break;
    case isDocuments:
      renderManageActions = PageTree;
      break;
    case isUsers:
      renderManageActions = FormUsers;
      break;
  }

  const modalOptions = {
    runAction: {
      component: renderManageActions,
      action: handleAction,
      title: 'Actions multiple',
      actionLabel: 'Appliquer',
      canDelete: !isDocuments && deletePermission ? 'deleteAction' : '',
    },
    deleteAction: {
      component: renderDeleteInput,
      action: handleDeleteAction,
      title: 'Supprimer entrées',
      actionLabel: 'Confirmer',
    },
  };

  return <Modal options={modalOptions} />;
};

export default ManageActions;
